import type { DateRangeShortcut } from '~/types';

export const useDateRangeShortcuts = () => {
  const { $i18n } = useNuxtApp();
  const { t, locale } = $i18n;

  const buildShortcuts = (
    selectedByKey: Partial<Record<DateRangeShortcut['dateShortcut'], boolean>> = {}
  ): Array<DateRangeShortcut> => {
    const today = new Date();

    return [
      {
        text: t('dateRangeShortcut.today'),
        range: [today, today],
        selected: selectedByKey.today ?? false,
        dateShortcut: 'today'
      },
      {
        text: t('dateRangeShortcut.yesterday'),
        range: [getDate().add(-1, 'day').toDate(), getDate().add(-1, 'day').toDate()],
        selected: selectedByKey.yesterday ?? false,
        dateShortcut: 'yesterday'
      },
      {
        text: t('dateRangeShortcut.last7Days'),
        range: [getDate().add(-6, 'day').toDate(), today],
        selected: selectedByKey.lastSevenDays ?? false,
        dateShortcut: 'lastSevenDays'
      },
      {
        text: t('dateRangeShortcut.last12Months'),
        range: [getDate().add(-12, 'month').add(1, 'day').toDate(), today],
        selected: selectedByKey.lastTwelveMonths ?? false,
        dateShortcut: 'lastTwelveMonths'
      },
      {
        text: t('dateRangeShortcut.previousMonth'),
        range: [
          getDate().add(-1, 'month').startOf('month').toDate(),
          getDate().add(-1, 'month').endOf('month').toDate()
        ],
        selected: selectedByKey.previousMonth ?? false,
        dateShortcut: 'previousMonth'
      },
      {
        text: t('dateRangeShortcut.thisMonth'),
        range: [getDate().startOf('month').toDate(), today],
        selected: selectedByKey.thisMonth ?? false,
        dateShortcut: 'thisMonth'
      },
      {
        text: t('dateRangeShortcut.thisQuarter'),
        range: [getDate().startOf('quarter').toDate(), today],
        selected: selectedByKey.thisQuarter ?? false,
        dateShortcut: 'thisQuarter'
      },
      {
        text: t('dateRangeShortcut.thisYear'),
        range: [getDate().startOf('year').toDate(), today],
        selected: selectedByKey.thisYear ?? false,
        dateShortcut: 'thisYear'
      }
    ];
  };

  const shortcuts = ref<Array<DateRangeShortcut>>(buildShortcuts());

  watch(
    () => locale.value,
    () => {
      const selectedByKey = shortcuts.value.reduce<
        Partial<Record<DateRangeShortcut['dateShortcut'], boolean>>
      >((acc, shortcut) => {
        acc[shortcut.dateShortcut] = shortcut.selected;
        return acc;
      }, {});

      shortcuts.value = buildShortcuts(selectedByKey);
    }
  );

  return { shortcuts, buildShortcuts };
};
